import React, { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Code2, Film, Layers, Play, Coffee, Sparkles } from "lucide-react";

// Pseudo-3D isometric scene — a stylized woman character standing at a PC workstation.
// Drawn in pure SVG + CSS with depth-parallax layers.
const StudioScene = () => {
  const ref = useRef(null);
  const videoRef = useRef(null);
  const [tilt, setTilt] = useState({ rx: 10, ry: -8 });
  const [mouse, setMouse] = useState({ x: 0.5, y: 0.5 });
  const [playing, setPlaying] = useState(false);

  // Video playback control
  React.useEffect(() => {
    if (videoRef.current) {
      if (playing) {
        videoRef.current.play().catch(() => {});
      } else {
        videoRef.current.pause();
      }
    }
  }, [playing]);

  React.useEffect(() => {
    const handleGlobalMove = (e) => {
      const clientX = e.type === 'mousemove' ? e.clientX : e.touches[0].clientX;
      const clientY = e.type === 'mousemove' ? e.clientY : e.touches[0].clientY;
      // Global eye tracking
      setMouse({
        x: clientX / window.innerWidth,
        y: clientY / window.innerHeight
      });
    };
    window.addEventListener("mousemove", handleGlobalMove);
    window.addEventListener("touchmove", handleGlobalMove);
    return () => {
      window.removeEventListener("mousemove", handleGlobalMove);
      window.removeEventListener("touchmove", handleGlobalMove);
    };
  }, []);

  const onMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const clientX = e.type === 'mousemove' ? e.clientX : e.touches[0].clientX;
    const clientY = e.type === 'mousemove' ? e.clientY : e.touches[0].clientY;
    const x = (clientX - r.left) / r.width;
    const y = (clientY - r.top) / r.height;
    setTilt({ rx: 10 + (0.5 - y) * 8, ry: -8 + (x - 0.5) * 10 });
  };
  const onLeave = () => {
    setTilt({ rx: 10, ry: -8 });
  };

  return (
    <section
      id="studio"
      ref={ref}
      onMouseMove={onMove}
      onTouchMove={onMove}
      onMouseLeave={onLeave}
      className="section relative overflow-hidden"
      style={{ background: "#0a0908" }}
    >
      {/* ambient gold haze */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(900px 500px at 30% 30%, rgba(228,199,122,0.08), transparent 60%), radial-gradient(700px 400px at 75% 70%, rgba(184,115,51,0.08), transparent 60%)"
        }}
      />

      <div className="container-xl">
        <div className="grid lg:grid-cols-[1fr_1.1fr] gap-16 items-center">
          {/* TEXT */}
          <div className="order-2 lg:order-1">
            <div className="flex items-center gap-4">
              <span className="font-impact text-[11px] tracking-[0.4em] text-[var(--gold)]">04</span>
              <span className="w-16 h-px bg-[var(--rule)]" />
              <span className="font-impact text-[11px] tracking-[0.4em] text-[var(--cream-muted)]">
                INSIDE THE STUDIO
              </span>
            </div>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9 }}
              className="mt-5 font-display text-[clamp(38px,5.2vw,72px)] leading-[1.02] text-[var(--cream)] balance"
            >
              One desk. <span className="italic gold-text">A thousand</span><br />
              decisions a day.
            </motion.h2>
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, delay: 0.15 }}
              className="mt-6 text-[15px] text-[var(--cream-muted)] leading-relaxed max-w-lg"
            >
              Every Meraki project passes through this chair. Chitra personally reviews every cut, every commit, every pixel — because taste scales only when someone refuses to let it dilute.
            </motion.p>

            <div className="mt-8 grid grid-cols-2 gap-4 max-w-md">
              <Bullet icon={<Film size={14} />} label="Edits daily" v="up to 6 hrs" />
              <Bullet icon={<Code2 size={14} />} label="Ships code" v="weekly sprints" />
              <Bullet icon={<Sparkles size={14} />} label="Reviews" v="every brief" />
              <Bullet icon={<Coffee size={14} />} label="Espressos" v="4 / day" />
            </div>

            <div className="mt-10 flex items-center gap-3 text-[var(--gold)]">
              <motion.span
                className="w-2 h-2 rounded-full bg-[var(--gold)]"
                animate={{ opacity: [1, 0.3, 1] }}
                transition={{ duration: 1.5, repeat: Infinity }}
              />
              <span className="font-impact text-[10px] tracking-[0.3em]">
                CURRENTLY · CUTTING AURORA V13
              </span>
            </div>
          </div>

          {/* ISOMETRIC SCENE */}
          <div
            ref={ref}
            onMouseMove={onMove}
            onMouseLeave={onLeave}
            className="relative w-full aspect-square order-1 lg:order-2"
            style={{ perspective: "1400px" }}
          >
            <motion.div
              animate={{ rotateX: tilt.rx, rotateY: tilt.ry }}
              transition={{ type: "spring", stiffness: 70, damping: 16 }}
              className="relative w-full h-full"
              style={{ transformStyle: "preserve-3d" }}
            >
              {/* Floor grid */}
              <div
                className="absolute left-1/2 -translate-x-1/2"
                style={{
                  bottom: "10%",
                  width: "90%",
                  height: "55%",
                  transform: "translate3d(-50%, 0, 0) rotateX(70deg)",
                  transformOrigin: "center bottom",
                  backgroundImage:
                    "linear-gradient(rgba(228,199,122,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(228,199,122,0.1) 1px, transparent 1px)",
                  backgroundSize: "40px 40px",
                  maskImage:
                    "radial-gradient(ellipse at center, black 40%, transparent 85%)",
                  WebkitMaskImage:
                    "radial-gradient(ellipse at center, black 40%, transparent 85%)"
                }}
              />

              {/* Back wall gradient */}
              <div
                className="absolute inset-0 rounded-sm"
                style={{
                  transform: "translateZ(-200px)",
                  background:
                    "radial-gradient(ellipse at center, rgba(228,199,122,0.12), transparent 60%)"
                }}
              />

              {/* DESK */}
              <div
                className="absolute left-1/2 -translate-x-1/2"
                style={{
                  bottom: "18%",
                  width: "70%",
                  height: "8%",
                  transform: "translate3d(-50%, 0, 40px)",
                  background: "linear-gradient(180deg, #1a1613, #0d0b09)",
                  border: "1px solid rgba(201,169,97,0.3)",
                  borderRadius: 2,
                  boxShadow:
                    "0 30px 60px rgba(0,0,0,0.5), inset 0 1px 0 rgba(228,199,122,0.2)"
                }}
              >
                {/* Desk front face */}
                <div
                  className="absolute left-2 right-2 top-full h-20"
                  style={{
                    background:
                      "linear-gradient(180deg, rgba(14,11,9,0.9) 0%, rgba(10,9,8,1) 100%)",
                    borderLeft: "1px solid rgba(201,169,97,0.15)",
                    borderRight: "1px solid rgba(201,169,97,0.15)",
                    transform: "rotateX(90deg) translateZ(-20px)",
                    transformOrigin: "top"
                  }}
                />
              </div>

              {/* MONITOR */}
              <div
                className="absolute left-1/2 -translate-x-1/2"
                style={{
                  bottom: "26%",
                  width: "44%",
                  height: "28%",
                  transform: "translate3d(-50%, 0, 20px)"
                }}
              >
                <div
                  className="w-full h-full rounded-sm overflow-hidden relative cursor-pointer group bg-black"
                  onClick={() => setPlaying(!playing)}
                >
                  {/* Video Element */}
                  <video
                    key="studio-video"
                    ref={videoRef}
                    className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${playing ? "opacity-100" : "opacity-60"}`}
                    loop
                    muted
                    playsInline
                    poster="https://images.unsplash.com/photo-1611540881474-bf7c8731bfd2?crop=entropy&cs=srgb&fm=jpg&q=80"
                  >
                    <source src="/videos/studio-edit.mp4" type="video/mp4" />
                  </video>

                  {/* Play Overlay (Visible when not playing) */}
                  {!playing && (
                    <div
                      className="absolute inset-0 flex items-center justify-center"
                      style={{
                        background:
                          "radial-gradient(100px 60px at center, rgba(0,0,0,0.1), rgba(0,0,0,0.5))"
                      }}
                    >
                      <motion.div
                        whileHover={{ scale: 1.1 }}
                        className="w-10 h-10 rounded-full flex items-center justify-center"
                        style={{
                          background: "rgba(228,199,122,0.15)",
                          border: "1px solid rgba(228,199,122,0.6)",
                          backdropFilter: "blur(4px)"
                        }}
                      >
                        <Play size={14} color="#e4c77a" fill="#e4c77a" />
                      </motion.div>
                    </div>
                  )}

                  {/* Screen UI overlay bits */}
                  <div className="absolute top-1 left-1.5 right-1.5 flex justify-between text-[7px] font-impact tracking-[0.2em] text-[var(--gold)]">
                    <span>· MERAKI EDIT</span>
                    <span>{playing ? "00:04:12:08" : "00:02:18:04"}</span>
                  </div>
                </div>
                {/* Monitor stand */}
                <div
                  className="absolute left-1/2 -translate-x-1/2 -bottom-2 w-[14%] h-2"
                  style={{
                    background: "#1a1613",
                    border: "1px solid rgba(201,169,97,0.2)"
                  }}
                />
              </div>

              {/* KEYBOARD (on desk) */}
              <div
                className="absolute left-1/2"
                style={{
                  bottom: "17%",
                  transform: "translate3d(-50%, 0, 50px) rotateX(55deg)",
                  transformOrigin: "center bottom",
                  width: "30%",
                  height: "5%",
                  background:
                    "linear-gradient(180deg, #1f1a16 0%, #0d0b09 100%)",
                  border: "1px solid rgba(201,169,97,0.3)",
                  borderRadius: 2
                }}
              />

              {/* LAMP — overhead with independent sway */}
              <div
                className="absolute"
                style={{
                  top: "10%",
                  right: "15%",
                  transform: "translateZ(60px)"
                }}
              >
                {/* Entire assembly sways together — independent infinite motion */}
                <motion.div
                  animate={{ x: [-140, 140, -140] }}
                  transition={{
                    duration: 10,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                  style={{ transformOrigin: "top center" }}
                >
                  {/* Ceiling mount */}
                  <div
                    className="w-16 h-2 rounded-full mx-auto"
                    style={{
                      background: "linear-gradient(180deg, #1a1613, #0a0908)",
                      border: "1px solid rgba(201,169,97,0.25)"
                    }}
                  />
                  <div
                    className="mx-auto w-px h-8"
                    style={{ background: "rgba(201,169,97,0.3)" }}
                  />
                  <div
                    className="w-20 h-4 mx-auto -mt-1 rounded-b-xl"
                    style={{
                      background:
                        "linear-gradient(180deg, #1a1613 0%, #0a0908 70%)",
                      border: "1px solid rgba(228,199,122,0.45)",
                      boxShadow: "0 10px 40px rgba(228,199,122,0.3)"
                    }}
                  />
                  {/* Light cone */}
                  <div
                    className="absolute top-[95%] left-1/2 -translate-x-1/2"
                    style={{
                      width: "180px",
                      height: "220px",
                      background:
                        "linear-gradient(180deg, rgba(228,199,122,0.35) 0%, transparent 100%)",
                      clipPath: "polygon(45% 0, 55% 0, 85% 100%, 15% 100%)",
                      filter: "blur(14px)",
                      opacity: 0.6
                    }}
                  />
                </motion.div>
              </div>

              {/* PLANT */}
              <div
                className="absolute"
                style={{
                  bottom: "25%",
                  right: "12%",
                  transform: "translateZ(60px)"
                }}
              >
                <div
                  className="w-8 h-6 rounded-t-lg"
                  style={{
                    background:
                      "linear-gradient(180deg, rgba(228,199,122,0.25), rgba(184,115,51,0.2))",
                    border: "1px solid rgba(201,169,97,0.3)"
                  }}
                />
                <PlantLeaves />
              </div>

              {/* CHARACTER — stylized woman figure */}
              <div
                className="absolute"
                style={{
                  bottom: "16%",
                  left: "15%",
                  width: "26%",
                  transform: "translateZ(80px)"
                }}
              >
                <WomanFigure lookX={(mouse.x - 0.5) * 2} lookY={(mouse.y - 0.5) * 2} />
              </div>

              {/* Coffee */}
              <div
                className="absolute"
                style={{
                  bottom: "23%",
                  left: "20%",
                  transform: "translateZ(70px)"
                }}
              >
                <div
                  className="w-5 h-6 rounded-b-sm relative"
                  style={{
                    background: "linear-gradient(180deg, #1a1613, #0a0908)",
                    border: "1px solid rgba(228,199,122,0.4)"
                  }}
                >
                  <div
                    className="absolute -right-1 top-1 w-1.5 h-3 rounded-r-full"
                    style={{
                      border: "1px solid rgba(228,199,122,0.4)",
                      borderLeft: "none"
                    }}
                  />
                  <motion.div
                    animate={{ opacity: [0.3, 0.7, 0.3], y: [-2, -8, -2] }}
                    transition={{ duration: 3, repeat: Infinity }}
                    className="absolute left-1/2 -top-2 -translate-x-1/2 w-[2px] h-2 rounded-full"
                    style={{
                      background: "rgba(245,240,230,0.3)",
                      filter: "blur(1.5px)"
                    }}
                  />
                </div>
              </div>

              {/* Floating labels */}
              <FloatLabel text="editing" icon={<Film size={10} />} left="62%" top="22%" z={100} delay={0} />
              <FloatLabel text="coding" icon={<Code2 size={10} />} left="22%" top="42%" z={90} delay={0.5} />
              <FloatLabel text="reviewing" icon={<Layers size={10} />} right="8%" top="52%" z={120} delay={1} accent />
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Bullet = ({ icon, label, v }) => (
  <div className="flex items-start gap-3">
    <span
      className="w-8 h-8 shrink-0 rounded-full flex items-center justify-center text-[var(--gold)] mt-0.5"
      style={{ border: "1px solid rgba(201,169,97,0.3)" }}
    >
      {icon}
    </span>
    <div>
      <div className="font-impact text-[9px] tracking-[0.3em] text-[var(--gold)]">
        {label.toUpperCase()}
      </div>
      <div className="text-[14px] text-[var(--cream)]">{v}</div>
    </div>
  </div>
);

const FloatLabel = ({ text, icon, left, right, top, z = 50, delay = 0, accent = false }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: [0, -6, 0] }}
    transition={{
      opacity: { duration: 0.7, delay: 0.3 + delay },
      y: { duration: 4, repeat: Infinity, ease: "easeInOut", delay }
    }}
    className="absolute flex items-center gap-1.5 px-2.5 py-1 rounded-full font-impact text-[9px] tracking-[0.2em] whitespace-nowrap"
    style={{
      left,
      right,
      top,
      transform: `translateZ(${z}px)`,
      background: accent ? "rgba(228,199,122,0.14)" : "rgba(20,17,15,0.85)",
      border: `1px solid ${accent ? "rgba(228,199,122,0.5)" : "rgba(201,169,97,0.25)"}`,
      color: accent ? "#e4c77a" : "var(--cream-muted)",
      backdropFilter: "blur(6px)"
    }}
  >
    {icon}
    {text.toUpperCase()}
  </motion.div>
);

const PlantLeaves = () => (
  <svg
    width="32"
    height="30"
    viewBox="0 0 40 40"
    style={{ position: "absolute", bottom: "100%", left: "50%", transform: "translateX(-50%)" }}
  >
    {[-18, -5, 8, 20].map((rot, i) => (
      <path
        key={i}
        d="M 20 40 Q 18 20 20 5 Q 22 20 20 40"
        fill="rgba(228,199,122,0.35)"
        stroke="rgba(228,199,122,0.6)"
        strokeWidth="0.5"
        transform={`rotate(${rot} 20 40)`}
      />
    ))}
  </svg>
);

// Stylized woman figure — drawn in pure SVG, 3D-rendered character vibe
// lookX: -1 (far left) to 1 (far right), lookY: -1 (top) to 1 (bottom)
const WomanFigure = ({ lookX = 0, lookY = 0 }) => {
  // Clamp the look range so eyes stay natural
  const ex = Math.max(-1, Math.min(1, lookX)) * 3;   // max 3px shift
  const ey = Math.max(-1, Math.min(1, lookY)) * 2;   // max 2px shift
  // Subtle head tilt to follow gaze
  const headX = Math.max(-1, Math.min(1, lookX)) * 1.5;
  const headY = Math.max(-1, Math.min(1, lookY)) * 0.8;

  return (
    <svg viewBox="0 0 200 400" style={{ width: "100%", height: "auto", filter: "drop-shadow(0 20px 40px rgba(0,0,0,0.5))" }}>
      <defs>
        <linearGradient id="skin" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#d8ad6e" />
          <stop offset="100%" stopColor="#9e7d4a" />
        </linearGradient>
        <linearGradient id="shirt" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#1a1613" />
          <stop offset="100%" stopColor="#0a0908" />
        </linearGradient>
        <linearGradient id="hair" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#1a1310" />
          <stop offset="100%" stopColor="#0a0706" />
        </linearGradient>
        <radialGradient id="cheek" cx="0.5" cy="0.5" r="0.5">
          <stop offset="0%" stopColor="#e4c77a" stopOpacity="0.6" />
          <stop offset="100%" stopColor="#e4c77a" stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* HEAD GROUP — moves subtly with gaze */}
      <g style={{ transform: `translate(${headX}px, ${headY}px)`, transition: "transform 0.15s ease-out" }}>
        {/* Back hair — moved up */}
        <ellipse cx="100" cy="74" rx="42" ry="50" fill="url(#hair)" />
        {/* Hair bun — moved up */}
        <circle cx="100" cy="36" r="18" fill="url(#hair)" />
        <circle cx="100" cy="36" r="16" fill="none" stroke="rgba(228,199,122,0.3)" strokeWidth="0.8" />

        {/* Neck */}
        <rect x="90" y="108" width="20" height="18" fill="url(#skin)" />

        {/* Face */}
        <ellipse cx="100" cy="82" rx="32" ry="38" fill="url(#skin)" />
        {/* Hair front fringe — moved up and adjusted */}
        <path
          d="M 68 60 Q 100 24 132 60 L 130 80 Q 115 63 100 65 Q 85 63 70 80 Z"
          fill="url(#hair)"
        />
        {/* Cheek blush */}
        <ellipse cx="82" cy="92" rx="8" ry="5" fill="url(#cheek)" />
        <ellipse cx="118" cy="92" rx="8" ry="5" fill="url(#cheek)" />

        {/* Eye whites (sclera) */}
        <ellipse cx="87" cy="82" rx="5" ry="4" fill="#f5f0e6" />
        <ellipse cx="113" cy="82" rx="5" ry="4" fill="#f5f0e6" />

        {/* Pupils — follow the mouse */}
        <ellipse cx={87 + ex} cy={82 + ey} rx="2.5" ry="3.5" fill="#0a0908" style={{ transition: "cx 0.12s ease-out, cy 0.12s ease-out" }} />
        <ellipse cx={113 + ex} cy={82 + ey} rx="2.5" ry="3.5" fill="#0a0908" style={{ transition: "cx 0.12s ease-out, cy 0.12s ease-out" }} />

        {/* Eye highlights — shift with pupils */}
        <circle cx={88 + ex * 0.6} cy={81 + ey * 0.4} r="1" fill="#ffffff" style={{ transition: "cx 0.12s ease-out, cy 0.12s ease-out" }} />
        <circle cx={114 + ex * 0.6} cy={81 + ey * 0.4} r="1" fill="#ffffff" style={{ transition: "cx 0.12s ease-out, cy 0.12s ease-out" }} />

        {/* Eyebrows */}
        <path d="M 81 73 Q 87 71 93 73" stroke="#0a0908" strokeWidth="1.5" fill="none" strokeLinecap="round" />
        <path d="M 107 73 Q 113 71 119 73" stroke="#0a0908" strokeWidth="1.5" fill="none" strokeLinecap="round" />
        {/* Nose */}
        <path d="M 100 88 L 97 100 Q 100 102 103 100 Z" fill="#b3874d" opacity="0.5" />
        {/* Lips */}
        <path
          d="M 92 107 Q 100 112 108 107 Q 100 110 92 107 Z"
          fill="#8a4a3a"
        />

        {/* Earrings */}
        <circle cx="68" cy="88" r="1.8" fill="#e4c77a" />
        <circle cx="132" cy="88" r="1.8" fill="#e4c77a" />
      </g>

      {/* Body / blazer */}
      <path
        d="M 60 126 L 140 126 L 152 230 L 48 230 Z"
        fill="url(#shirt)"
        stroke="rgba(228,199,122,0.15)"
        strokeWidth="0.5"
      />
      {/* Lapels */}
      <path d="M 85 126 L 100 170 L 85 210 L 75 180 Z" fill="#0a0706" />
      <path d="M 115 126 L 100 170 L 115 210 L 125 180 Z" fill="#0a0706" />
      {/* Inner shirt */}
      <path d="M 90 130 L 100 170 L 110 130 Z" fill="#f5f0e6" />
      {/* Lapel gold pin */}
      <circle cx="88" cy="150" r="2" fill="#e4c77a" />

      {/* Left arm (raised, typing) */}
      <path
        d="M 60 132 Q 40 170 55 215 L 70 235 L 80 232 L 72 210 Q 58 180 75 145 Z"
        fill="url(#shirt)"
        stroke="rgba(228,199,122,0.15)"
        strokeWidth="0.5"
      />
      {/* Left hand */}
      <ellipse cx="75" cy="235" rx="8" ry="9" fill="url(#skin)" />

      {/* Right arm (raised, typing) */}
      <path
        d="M 140 132 Q 160 170 145 215 L 130 235 L 120 232 L 128 210 Q 142 180 125 145 Z"
        fill="url(#shirt)"
        stroke="rgba(228,199,122,0.15)"
        strokeWidth="0.5"
      />
      {/* Right hand */}
      <ellipse cx="125" cy="235" rx="8" ry="9" fill="url(#skin)" />

      {/* Legs (partial — visible beneath desk edge) */}
      <path
        d="M 70 230 L 95 230 L 92 310 L 72 310 Z"
        fill="#1f1a16"
      />
      <path
        d="M 105 230 L 130 230 L 128 310 L 108 310 Z"
        fill="#1f1a16"
      />

      {/* Shadow below figure */}
      <ellipse cx="100" cy="315" rx="50" ry="5" fill="#000" opacity="0.5" />
    </svg>
  );
};

export default StudioScene;
